import React, { useEffect, useState } from "react";

import { incomplete, useGetAdminSettings, useUpdateAdminSettings } from "utils";

import { WEBINAR_LINK_DEFAULT } from "consts";

import { appVersions } from "enums";

import { AdminSection, Alert, Button, Col, Form, Input, InputNumber, MainPage, Option, Row, Select } from "components";

export const SettingDetails = () => {
  const [loading, setLoading] = useState(false);
  const { data: adminSettings, isLoading: isLoadingAdminSettings } = useGetAdminSettings();
  const { mutateAsync: updateAdminSettings, isPending: isPendingUpdateAdminSettings } = useUpdateAdminSettings();

  const [form] = Form.useForm();

  const complete = (v) => {
    setLoading(true);
    updateAdminSettings({
      ...v,
    }).finally(() => {
      setLoading(false);
    });
  };

  // Initialize form when settings are loaded
  useEffect(() => {
    if (adminSettings && !isLoadingAdminSettings) {
      form.setFieldsValue({
        minCoverCrop: adminSettings.minCoverCrop || null,
        emailSending: adminSettings.emailSending,
        pointPriceCategory1: adminSettings.pointPriceCategory1,
        pointPriceCategory2: adminSettings.pointPriceCategory2,
        pointPriceCategory3: adminSettings.pointPriceCategory3,
        pointPriceCategory4: adminSettings.pointPriceCategory4,
        referralValue: adminSettings.referralValue,
        firstMethodReferralValue: adminSettings.firstMethodReferralValue,
        firstYearlyDataReferralValue: adminSettings.firstYearlyDataReferralValue,
        sequestrationZeroValue: adminSettings.sequestrationZeroValue,
        minAppVersion: adminSettings.minAppVersion,
        webinarLink: adminSettings.webinarLink,
        ydeYieldFactor: adminSettings.ydeYieldFactor,
        bauYieldFactor: adminSettings.bauYieldFactor,
      });
    }
  }, [adminSettings, isLoadingAdminSettings, form]);

  return (
    <MainPage
      loading={loading || isLoadingAdminSettings || isPendingUpdateAdminSettings}
      pageName={"admin"}
      beforePage={
        <Alert
          className="alert-banner center sticky"
          description="Proceed with caution. Modifying settings here may have broad or unexpected impacts on the platform and users."
          icon=""
          closable={false}
          style={{ backgroundColor: "#ff845e" }}
        />
      }
    >
      <Row>
        <Col>
          <Form form={form} name="confirm" onFinish={(v) => complete(v)} onFinishFailed={incomplete}>
            <Row>
              <Col>
                <AdminSection
                  headline="Minimum cover crop"
                  description="Choose a baseline cover crop, from which we will calculate the red zone deductions. Disabled for now until this is revisited for FCPO."
                  extra={
                    <Select
                      name="minCoverCrop"
                      label="Minimum cover crop"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.minCoverCrop || null}
                      className="no-suffix left"
                      disabled
                      showSearch
                    >
                      {/* {crops?.map((crop, i) => (
                        <Option key={i} value={crop.id}>
                          {crop.name}
                        </Option>
                      ))} */}
                    </Select>
                  }
                />
                <AdminSection
                  headline="Email sending"
                  description="Enable, pause or limit email sending. Priority 1 items are the most important. This sets the max priority allowed to send, so if you set it to 2 then prio 3 will not send."
                  extra={
                    <Select
                      name="emailSending"
                      label="Max email priority to send"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.emailSending}
                      className="no-suffix left"
                    >
                      <Option value={0}>Block sending of all emails</Option>
                      <Option value={1}>Only send priority 1 emails</Option>
                      <Option value={2}>Only send priority 1 and 2 emails</Option>
                      <Option value={3}>Send all emails</Option>
                    </Select>
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="Funded point value category 1 (Removals)"
                  description="Specify the euro value of “Actual Removals”. Any changes will only apply to new funded points received by users."
                  extra={
                    <InputNumber
                      name="pointPriceCategory1"
                      label="Point value"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.pointPriceCategory1}
                      precision={2}
                      step={0.01}
                      suffix="€"
                      min={0}
                    />
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="Funded point value category 2 (Removals)"
                  description="Specify the euro value of the “Offsetting Calculation Helper“ . Any changes will only apply to new funded points received by users."
                  extra={
                    <InputNumber
                      name="pointPriceCategory2"
                      label="Point value"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.pointPriceCategory2}
                      precision={2}
                      step={0.01}
                      suffix="€"
                      min={0}
                    />
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="Funded point value category 3 (Reductions)"
                  description="Specify the euro value of “Reductions compared to last year”. Any changes will only apply to new funded points received by users."
                  extra={
                    <InputNumber
                      name="pointPriceCategory3"
                      label="Point value"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.pointPriceCategory3}
                      precision={2}
                      step={0.01}
                      suffix="€"
                      min={0}
                    />
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="Funded point value category 4 (Reductions)"
                  description="Specify the euro value of the “Offsetting Calculation Helper”. Any changes will only apply to new funded points received by users."
                  extra={
                    <InputNumber
                      name="pointPriceCategory4"
                      label="Point value"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.pointPriceCategory4}
                      precision={2}
                      step={0.01}
                      suffix="€"
                      min={0}
                    />
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="Referral value"
                  description="Specify the euro value of a referral. Any changes will only apply to new referrals."
                  extra={
                    <InputNumber
                      name="referralValue"
                      label="Referral value"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.referralValue}
                      precision={2}
                      step={0.01}
                      suffix="€"
                      min={0}
                    />
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="First method referral value"
                  description="Specify the euro value of the first method completed by a referred user. The payment will be made to the referring user, once the method has been verified by an admin. Any changes will only apply to new referrals."
                  extra={
                    <InputNumber
                      name="firstMethodReferralValue"
                      label="First method referral value"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.firstMethodReferralValue}
                      precision={2}
                      step={0.01}
                      suffix="€"
                      min={0}
                    />
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="First yearly data referral value"
                  description="Specify the euro value of the first module yearly data entry completed by a referred user. The payment will be made to the referring user, once the method has been verified by an admin. Any changes will only apply to new referrals."
                  extra={
                    <InputNumber
                      name="firstYearlyDataReferralValue"
                      label="First yearly data referral value"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.firstYearlyDataReferralValue}
                      precision={2}
                      step={0.01}
                      suffix="€"
                      min={0}
                    />
                  }
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="Default sequestration zero value"
                  description="Specify the default sequestration value of the so called zero value. Usually the zero value for each individual region will be used for footprint calculations, but this value will serve as a fallback."
                  extra={<InputNumber name="sequestrationZeroValue" label="Sequestration zero value" initialValue={adminSettings?.sequestrationZeroValue} />}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AdminSection
                  headline="Minimum app version"
                  description="Force users to upgrade to the newest version of the app if there are breaking changes. This should only be done when necessary, as it interfers with users."
                  extra={
                    <Select
                      name="minAppVersion"
                      label="Min app version"
                      rules={[{ required: true }]}
                      initialValue={adminSettings?.minAppVersion}
                      className="no-suffix left"
                    >
                      {appVersions.map((item, i) => (
                        <Option key={i} value={`${item.major}.${item.minor}`}>
                          <small>
                            <b>
                              {item.major}.{item.minor}
                            </b>{" "}
                            | {item.date}
                          </small>
                        </Option>
                      ))}
                    </Select>
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <AdminSection
                  headline="Link to the Webinar"
                  description={`You can change the link to the webinar presented on our Landing MainPage. 
                  If you leave this input empty this link will be set as default:
                  ${WEBINAR_LINK_DEFAULT} 
                  Make sure that the link starts with "https://", btw "www." is not required.
                  `}
                  extra={
                    <Input
                      name="webinarLink"
                      label="Webinar Link"
                      initialValue={adminSettings?.webinarLink}
                      className="no-suffix"
                      onPressEnter={(e) => {
                        e.preventDefault();
                      }}
                    />
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <AdminSection
                  headline="YDE & BAU yield factors"
                  description="Edit YDE and BAU multiplcation factors here"
                  extra={
                    <Row>
                      <Col sm={12} md={12}>
                        <InputNumber name="ydeYieldFactor" rules={[{ required: true }]} label="YDE yield factor" initialValue={adminSettings?.ydeYieldFactor} />
                      </Col>
                      <Col sm={12} md={12}>
                        <InputNumber name="bauYieldFactor" rules={[{ required: true }]} label="BAU yield factor" initialValue={adminSettings?.bauYieldFactor} />
                      </Col>
                    </Row>
                  }
                />
              </Col>
            </Row>

            <Row className="margin-top-200">
              <Col>
                <Form.Item>
                  <Button type="primary" htmlType="submit" disabled={isPendingUpdateAdminSettings} loading={isPendingUpdateAdminSettings}>
                    Update settings
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </MainPage>
  );
};
